import { useEffect, useState } from 'react';
import api from '../lib/api';
import { formatAddress, ITANI_NETWORK } from '../lib/blockchain';

const getWalletAddress = (props) =>
  props?.account ||
  props?.wallet?.account ||
  props?.wallet?.address ||
  props?.walletState?.account ||
  props?.walletState?.address ||
  props?.metaMask?.account ||
  props?.metaMask?.address ||
  '';

const getChainId = (props) =>
  props?.chainId || props?.wallet?.chainId || props?.walletState?.chainId || props?.metaMask?.chainId;

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.apps)) return value.apps;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const formatValue = (value, fallback = '—') => {
  if (value === undefined || value === null || value === '') return fallback;
  return `${value}`;
};

export default function Settings(props) {
  const account = getWalletAddress(props);
  const chainId = getChainId(props);

  const [apps, setApps] = useState([]);
  const [chainInfo, setChainInfo] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setError('');

      try {
        const [appsResult, infoResult, healthResult] = await Promise.allSettled([
          api.sso.apps(),
          api.chain.info(),
          api.health(),
        ]);

        if (appsResult.status === 'fulfilled') setApps(asArray(appsResult.value));
        if (infoResult.status === 'fulfilled') setChainInfo(infoResult.value);
        if (healthResult.status === 'fulfilled') setHealth(healthResult.value);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Unable to load settings data.');
      }
    };

    load();
  }, []);

  return (
    <div className="space-y-6">
      <section className="glass p-6">
        <p className="text-sm uppercase tracking-wide text-slate-400">Settings</p>
        <h1 className="mt-2 text-3xl font-semibold text-white">Environment and security overview</h1>
        <p className="mt-2 text-slate-300">
          This page is informational and mirrors the frontend environment currently connected to the API.
        </p>
      </section>

      {error ? (
        <section className="glass border border-rose-500/30 p-4 text-rose-200">{error}</section>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article className="glass p-5">
          <p className="text-sm text-slate-400">API URL</p>
          <h2 className="mt-2 text-lg font-semibold break-all text-white">{api.baseUrl}</h2>
        </article>

        <article className="glass p-5">
          <p className="text-sm text-slate-400">Wallet</p>
          <h2 className="mt-2 text-lg font-semibold text-white">
            {account ? formatAddress(account) : 'Not connected'}
          </h2>
        </article>

        <article className="glass p-5">
          <p className="text-sm text-slate-400">Wallet chain ID</p>
          <h2 className="mt-2 text-lg font-semibold text-white">{formatValue(chainId, 'Unavailable')}</h2>
        </article>

        <article className="glass p-5">
          <p className="text-sm text-slate-400">Backend health</p>
          <h2 className="mt-2 text-lg font-semibold text-white">
            {formatValue(health?.status || health?.message, 'Unknown')}
          </h2>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">Network details</h2>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">Expected chain</span>
              <span className="text-white">{ITANI_NETWORK.chainName}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">Expected chain ID</span>
              <span className="text-white">{ITANI_NETWORK.chainId}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">API network</span>
              <span className="text-white">
                {formatValue(chainInfo?.network || chainInfo?.chain || chainInfo?.name, 'Unknown')}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-slate-400">RPC / provider</span>
              <span className="max-w-[220px] text-right text-white">
                {formatValue(chainInfo?.rpc_url || chainInfo?.provider || chainInfo?.endpoint, 'Not exposed')}
              </span>
            </div>
          </div>
        </article>

        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">SSO apps</h2>
          {apps.length ? (
            <div className="mt-4 space-y-3">
              {apps.map((app, index) => (
                <div key={app?.id || app?.name || index} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <p className="font-medium text-white">{formatValue(app?.name || app?.id, 'Unnamed app')}</p>
                  <p className="mt-1 text-sm text-slate-400">
                    {formatValue(app?.description || app?.origin || app?.url, 'No extra metadata')}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-slate-300">No SSO app list was returned by the backend.</p>
          )}
        </article>
      </section>

      <section className="glass p-6">
        <h2 className="text-xl font-semibold text-white">Security reminders</h2>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-slate-300">
          <li>Verify the connected wallet chain before approving transactions.</li>
          <li>Expose production API URLs only through trusted environment configuration.</li>
          <li>Do not rely on this frontend for privileged admin actions unless the backend enforces auth.</li>
          <li>Rotate any leaked secrets and keep webhook keys outside version control.</li>
        </ul>
      </section>
    </div>
  );
}